/**
 * Responsibility: listings priced under their own grade's market.
 *
 * Every row is one grade of one card, not one card - the same Charizard
 * can be an outlier raw and fairly priced in a PSA 10, and those are two
 * different things to buy. The grade is the second column for that
 * reason, and the filter above the table is on it.
 *
 * Both averages are shown because a row had to beat both to be here, and
 * which one it barely cleared is the difference between a real find and a
 * card whose market is drifting down.
 *
 * The row opens the listing, not a dialog. An asking price under the
 * market is sometimes a bargain and sometimes a damaged card, a reprint or
 * a wrong listing, and nothing here can tell them apart - so the only
 * honest next step is to go and look at it.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LaunchIcon from '@mui/icons-material/Launch';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import type { Outlier } from '../../api/client';
import { CardThumb } from './CardThumb';
import { gradeBucketLabel } from './gradeBuckets';
import { money, percent } from './money';

const THUMB_WIDTH = 46;

/** Below this many sales the average is thin enough to say so. */
const THIN_EVIDENCE = 6;

type OutlierTableProps = {
  rows: Outlier[];
  loading: boolean;
  maxHeight?: number;
};

/**
 * Column widths are measured, not guessed: headings never wrap, so a
 * heading that does not fit its column is clipped. `tests/table-headers.mjs`
 * renders this table in a browser and fails on a clipped one.
 */
export function OutlierTable({ rows, loading, maxHeight }: OutlierTableProps) {
  const columns: DataColumn<Outlier>[] = [
    {
      id: 'card',
      label: 'Card',
      render: (row) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, py: 0.5 }}>
          <CardThumb src={row.image_url} alt={row.card_name} width={THUMB_WIDTH} />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {row.card_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.listing_title}
            </Typography>
          </Stack>
        </Stack>
      ),
      sortValue: (row) => row.card_name,
    },
    {
      id: 'grade',
      label: 'Grade',
      width: 104,
      render: (row) => (
        <Chip
          size="small"
          variant="outlined"
          // The bucket already carries the grader - "PSA 10", "CGC 9.5" -
          // because that is what it was compared against. Adding
          // `grading_company` in front of it read "PSA PSA 10", and
          // worse, implied the two were separate facts.
          label={gradeBucketLabel(row.bucket)}
        />
      ),
      sortValue: (row) => row.bucket,
    },
    {
      id: 'asking',
      label: 'Asking',
      width: 104,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        >
          {money(row.asking)}
        </Typography>
      ),
      sortValue: (row) => Number(row.asking),
    },
    {
      id: 'recent',
      label: 'Last 5',
      width: 100,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {money(row.recent_average)}
        </Typography>
      ),
      sortValue: (row) => Number(row.recent_average),
    },
    {
      id: 'overall',
      label: 'All sales',
      width: 112,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {money(row.overall_average)}
        </Typography>
      ),
      sortValue: (row) => Number(row.overall_average),
    },
    {
      id: 'discount',
      label: 'Under',
      width: 96,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          color="success.main"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        >
          {percent(row.discount, 0)}
        </Typography>
      ),
      sortValue: (row) => Number(row.discount),
    },
    {
      id: 'sales',
      label: 'Sales',
      width: 94,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          color={row.sales_count < THIN_EVIDENCE ? 'warning.main' : 'text.primary'}
        >
          {row.sales_count}
        </Typography>
      ),
      sortValue: (row) => row.sales_count,
    },
    {
      id: 'open',
      label: '',
      width: 56,
      align: 'right',
      render: (row) => (
        <ToolbarIconButton
          aria-label="Open the listing"
          onClick={(event) => {
            event.stopPropagation();
            window.open(row.listing_url, '_blank', 'noopener,noreferrer');
          }}
        >
          <LaunchIcon fontSize="small" />
        </ToolbarIconButton>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      loading={loading}
      rowKey={(row) => `${row.product_key}:${row.bucket}`}
      onRowClick={(row) => window.open(row.listing_url, '_blank', 'noopener,noreferrer')}
      maxHeight={maxHeight}
      emptyMessage="Nothing under its own market in this scan. That is the usual answer - try a broader phrase."
    />
  );
}
